import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu } from 'lucide-react';
import './ImagingHeader.css';

// Imaging-page navigation entries surfaced in both the desktop nav and
// the mobile pulldown. Kept as a module-level constant so the two render
// paths stay in sync. `key` is the stable identifier callers pass via the
// `activeTab` prop so the active state is decoupled from the URL.
const IMAGING_NAV_ITEMS = [
    { key: 'images', label: 'Your Images', path: '/images' },
    { key: 'shares', label: 'Your Shares', path: '/images/shares' },
    { key: 'upload', label: 'Upload', path: '/images/upload' },
];

function navLabel(item, counts) {
    const count = counts?.[item.key];
    if (!Number.isFinite(count)) return item.label;
    return (
        <>
            {item.label}
            <span className="imaging-nav-count">({count})</span>
        </>
    );
}

function ImagingHeader({
    title = 'Imaging',
    actions = null,
    inlineActions = false,
    activeTab,
    counts,
    attentionTab = null,
    attentionToken = 0,
}) {
    const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
    const navigate = useNavigate();
    const showActionsInline = Boolean(actions) && inlineActions;
    const showActionsStrip = Boolean(actions) && !inlineActions;

    const handleNavClick = (item) => {
        setIsMobileNavOpen(false);
        if (item.key !== activeTab) navigate(item.path);
    };

    const renderNavButton = (item) => {
        const active = activeTab === item.key;
        const attention = attentionTab === item.key;
        const className = [
            'imaging-nav-button',
            active ? 'is-active' : '',
            attention ? 'is-attention' : '',
        ].filter(Boolean).join(' ');
        return (
            <button
                key={attention ? `${item.key}:${attentionToken}` : item.key}
                type="button"
                className={className}
                data-active={active ? 'true' : undefined}
                aria-current={active ? 'page' : undefined}
                onClick={() => handleNavClick(item)}
            >
                {navLabel(item, counts)}
            </button>
        );
    };

    return (
        <header className="imaging-header">
            <div className="imaging-header-top">
                <div className="imaging-header-title-group">
                    <button
                        type="button"
                        className="imaging-mobile-toggle"
                        aria-label="Toggle menu"
                        aria-expanded={isMobileNavOpen}
                        onClick={() => setIsMobileNavOpen((open) => !open)}
                    >
                        <Menu size={20} strokeWidth={2} />
                    </button>
                    <h1 className="imaging-header-title">{title}</h1>
                </div>
                <nav className="imaging-nav" aria-label="Imaging sections">
                    {IMAGING_NAV_ITEMS.map(renderNavButton)}
                </nav>
                {showActionsInline ? (
                    <div className="imaging-header-top-actions">
                        {actions}
                    </div>
                ) : (
                    <div className="imaging-header-spacer" aria-hidden="true" />
                )}
            </div>
            <div
                className={`imaging-mobile-menu${isMobileNavOpen ? ' is-open' : ''}`}
                aria-hidden={!isMobileNavOpen}
            >
                {IMAGING_NAV_ITEMS.map(renderNavButton)}
            </div>
            {showActionsStrip && (
                <div className="imaging-header-actions">
                    <div className="imaging-header-actions-inner">
                        {actions}
                    </div>
                </div>
            )}
        </header>
    );
}

export default ImagingHeader;
